import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, type FC } from "react";
import { useForm } from "react-hook-form";

import { useRootDispatch } from "@ds/hooks/redux-hooks";

import { playersActions } from "@ds/modules/devices/players/redux/slice";
import { hasRoleStep } from "@ds/modules/devices/players/utils/new-player-role";
import { createValidationSchema } from "@ds/modules/devices/players/utils/validation-schema";

import { WizardPage, type WizardChildStepProps } from "@ds/components/wizard";

import { PlayerExperienceControl } from "../controls/player-experience-control";
import { useCreatePlayer } from "./create-player-context";

export const Step2Experience: FC<WizardChildStepProps & { roles: FormationRoleMap }> = ({
  isActive,
  nextStep,
  previousStep,
  roles,
  ...rest
}) => {
  const [currentPlayerData, submitPlayerData] = useCreatePlayer();
  const { control, getValues, setValue, handleSubmit, reset } = useForm<PlayerFormData>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: currentPlayerData,
    resolver: yupResolver(createValidationSchema.step2),
  });

  useEffect(() => {
    if (isActive) {
      reset(currentPlayerData);
    }
  }, [currentPlayerData, isActive, reset]);

  const dispatch = useRootDispatch();
  const onNextClickHandler = useCallback(() => {
    const dataToSubmit = getValues();
    // a role picked for another experience doesn't carry over
    const isSameExperience = dataToSubmit.experience?.id === currentPlayerData.experience?.id;
    submitPlayerData({ ...dataToSubmit, formation: isSameExperience ? dataToSubmit.formation : undefined });

    // the Role page adds the player once its role is picked
    if (!hasRoleStep(roles, dataToSubmit.experience)) {
      dispatch(playersActions.createPlayer({ player: dataToSubmit.player, pairingCode: dataToSubmit.pairingCode }));
    }

    if (nextStep) {
      nextStep();
    }
  }, [dispatch, submitPlayerData, getValues, nextStep, currentPlayerData.experience?.id, roles]);

  const onBackClickHandler = useCallback(() => {
    submitPlayerData(getValues());
    if (previousStep) {
      previousStep();
    }
  }, [submitPlayerData, getValues, previousStep]);

  return (
    <WizardPage
      {...rest}
      title="Assign experience"
      previousStep={onBackClickHandler}
      nextStep={handleSubmit(onNextClickHandler)}
    >
      <PlayerExperienceControl control={control} setValue={setValue} />
    </WizardPage>
  );
};
